import { dailyBurn } from "./body";
import { addDays, daysBetween, todayStr } from "./date";
import { KCAL_PER_KG_FAT, gapToTarget } from "./deficit";
import type { DeviceAnswers } from "./eating";
import { dayKcal } from "./energy";
import { dayMark, type DayMark } from "./dayMarks";
import { deficitTarget, entriesFor, foodBudget, foodOn, totalsOf } from "./intake";
import { ewma, trendRate, weightSeries } from "./trend";
import type { KayaState } from "./types";

/**
 * The energy ledger: what went in against what went out, per day and over weeks, and what that
 * should have done to the scale.
 *
 * Every field that Kaya could not measure is null, never 0 (invariant 11). A day with no food
 * logged is "not logged", not "ate nothing" — treating it as zero would book a 2,000 kcal deficit
 * on every forgotten day and make the prediction absurdly optimistic.
 */

/** Weight as of a date, so a past day's burn is priced at what the user weighed then rather than
 *  silently re-priced every time a new weigh-in lands. */
export function weightOn(S: KayaState, ds: string): number {
  let best: { date: string; w: number } | null = null;
  for (const m of S.measures) {
    if (!m.w || m.date > ds) continue;
    if (!best || m.date > best.date) best = { date: m.date, w: m.w };
  }
  return best ? best.w : S.profile.startWeight;
}

/** Kcal eaten on a day, or null when nothing was logged or food tracking is off. */
export function dayIn(S: KayaState, ds: string): number | null {
  if (!foodOn(S)) return null;
  const entries = entriesFor(S, ds);
  return entries.length ? totalsOf(entries).kcal : null;
}

export interface DayLedger {
  ds: string;
  in: number | null;
  /** Estimated total burn: resting + everyday living + the work Kaya measured. */
  out: number | null;
  /** in − out; negative is a deficit. Null unless both sides are known. */
  net: number | null;
  budget: number | null;
  remaining: number | null;
  /** Sessions, treadmill, walks. Steps are deliberately not priced — `baseline` covers them. */
  measuredOut: number;
  restingOut: number | null;
  rest: number | null;
  baseline: number | null;
  /** Untracked or a fast day (ADR-049). The day's own figures stand — what was logged was
   *  eaten — but no average may use it: see `averagable`. */
  mark: DayMark | null;
}

/**
 * Whether a day may join an average of In against Out: both sides known and the day not marked.
 * An untracked day's In is a part of the day read as the whole, and a fast day is kept out by the
 * person's own choice; either would move the average by a day that is not like the others.
 */
export function averagable(d: DayLedger): boolean {
  return d.in !== null && d.out !== null && d.mark === null;
}

export function dayLedger(S: KayaState, ds: string, today = new Date(), device?: DeviceAnswers): DayLedger {
  const inK = dayIn(S, ds);
  const measuredOut = dayKcal(S, ds);
  const burn = dailyBurn(S, weightOn(S, ds), measuredOut, today);
  const mark = dayMark(S, ds);
  if (!burn) {
    return {
      ds,
      in: inK,
      out: null,
      net: null,
      budget: null,
      remaining: null,
      measuredOut,
      restingOut: null,
      rest: null,
      baseline: null,
      mark,
    };
  }
  const out = burn.total;
  const budget = foodBudget(out, measuredOut, deficitTarget(S, today, device, ds));
  return {
    ds,
    in: inK,
    out,
    net: inK === null ? null : inK - out,
    budget,
    remaining: budget - (inK ?? 0),
    measuredOut,
    restingOut: burn.rest + burn.baseline,
    rest: burn.rest,
    baseline: burn.baseline,
    mark,
  };
}

export interface WeekLedger {
  days: DayLedger[];
  /** Days with both sides known — the only days an average may use. */
  paired: number;
  inDays: number;
  sumIn: number;
  sumOut: number;
  avgIn: number | null;
  avgOut: number | null;
  avgNet: number | null;
}

function summarise(days: DayLedger[]): Omit<WeekLedger, "days"> {
  const paired = days.filter(averagable);
  const sumIn = paired.reduce((a, d) => a + (d.in ?? 0), 0);
  const sumOut = paired.reduce((a, d) => a + (d.out ?? 0), 0);
  const n = paired.length;
  return {
    paired: n,
    inDays: days.filter((d) => d.in !== null && d.mark === null).length,
    sumIn,
    sumOut,
    avgIn: n ? Math.round(sumIn / n) : null,
    avgOut: n ? Math.round(sumOut / n) : null,
    avgNet: n ? Math.round((sumIn - sumOut) / n) : null,
  };
}

/** The seven days ending on `endDs`, oldest first. */
export function weekLedger(S: KayaState, endDs: string, today = new Date()): WeekLedger {
  const days: DayLedger[] = [];
  for (let i = 6; i >= 0; i--) days.push(dayLedger(S, addDays(endDs, -i), today));
  return { days, ...summarise(days) };
}

export interface LedgerProjection {
  paired: number;
  avgIn: number;
  avgOut: number;
  avgNet: number;
  /** What the log says the scale should be doing; negative is a loss. */
  predictedKgPerWeek: number;
  /** What the trend weight is actually doing, or null without enough weigh-ins. */
  observedKgPerWeek: number | null;
  /** The daily burn that would make the log and the scale agree. */
  impliedOut: number | null;
  /** impliedOut ÷ Kaya's estimate. Below 1: burn over-estimated or food under-logged. */
  outBias: number | null;
}

const LEDGER_WINDOW_DAYS = 28;
const LEDGER_TAKE = 14;
const MIN_PAIRED = 7;
/** An implied burn from under two weeks of trend is mostly water, and would swing wildly. */
const MIN_TREND_SPAN_FOR_IMPLIED = 14;

/**
 * Expected against actual. Today is left out: its food log is still being written, and counting
 * a half-logged day would book a deficit that has not happened.
 */
export function projectFromLedger(S: KayaState, today = new Date()): LedgerProjection | null {
  const ds = todayStr(today);
  const paired: DayLedger[] = [];
  for (let i = 1; i <= LEDGER_WINDOW_DAYS && paired.length < LEDGER_TAKE; i++) {
    const L = dayLedger(S, addDays(ds, -i), today);
    if (averagable(L)) paired.push(L);
  }
  if (paired.length < MIN_PAIRED) return null;
  const s = summarise(paired);
  if (s.avgIn === null || s.avgOut === null || s.avgNet === null) return null;

  const predictedKgPerWeek = (s.avgNet * 7) / KCAL_PER_KG_FAT;
  const trend = ewma(weightSeries(S));
  const observedKgPerWeek = trendRate(trend, 21, ds);

  const recent = trend.filter((p) => {
    const age = daysBetween(p.date, ds);
    return age >= 0 && age <= LEDGER_WINDOW_DAYS;
  });
  const span = recent.length >= 2 ? daysBetween(recent[0].date, recent[recent.length - 1].date) : 0;
  const impliedOut =
    observedKgPerWeek === null || span < MIN_TREND_SPAN_FOR_IMPLIED
      ? null
      : Math.round(s.avgIn - (observedKgPerWeek / 7) * KCAL_PER_KG_FAT);

  return {
    paired: s.paired,
    avgIn: s.avgIn,
    avgOut: s.avgOut,
    avgNet: s.avgNet,
    predictedKgPerWeek,
    observedKgPerWeek,
    impliedOut,
    outBias: impliedOut === null || !s.avgOut ? null : impliedOut / s.avgOut,
  };
}

/** A labelled what-if from one day's balance — never a forecast; one day is not a diet. */
export function ifEveryDayLikeToday(
  S: KayaState,
  today = new Date()
): { net: number; kgPerWeek: number; etaDays: number | null; etaDate: string | null } | null {
  const ds = todayStr(today);
  const L = dayLedger(S, ds, today);
  if (L.net === null) return null;
  const kgPerWeek = (L.net * 7) / KCAL_PER_KG_FAT;
  const gap = gapToTarget(S, weightOn(S, ds));
  const etaDays = gap && kgPerWeek < 0 ? Math.round(gap.kgToLose / (-kgPerWeek / 7)) : null;
  return { net: L.net, kgPerWeek, etaDays, etaDate: etaDays === null ? null : addDays(ds, etaDays) };
}
